/**
 * FarmlyRichTextField — visual TipTap editor for Farmly Markdown v1.
 *
 * Supports: bold, italic, bullet list, ordered list, paragraphs.
 * Disallows: headings, blockquote, code, images, embeds, arbitrary HTML.
 *
 * Value is Farmly Markdown v1 (string). Converted to/from TipTap HTML internally.
 * SoT: docs/architecture/content-formatting.md
 */

import { useEffect, useRef, useCallback } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import HardBreak from '@tiptap/extension-hard-break';
import { Bold, Italic, List, ListOrdered } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import {
  farmlyMarkdownToHtml,
  htmlToFarmlyMarkdown,
  farmlyMarkdownTextLength,
} from '@/lib/content/farmlyMarkdown';

interface FarmlyRichTextFieldProps {
  label: string;
  /** Farmly Markdown v1 string */
  value: string;
  /** Called with Farmly Markdown v1 string */
  onChange: (markdown: string) => void;
  maxLength?: number;
  placeholder?: string;
  disabled?: boolean;
  /** Minimum editor height in px */
  minHeightPx?: number;
  /** Show character length warning below threshold */
  warnBelow?: number;
  warnMessage?: string;
  id?: string;
}

export function FarmlyRichTextField({
  label,
  value,
  onChange,
  maxLength = 5000,
  placeholder = 'Skriv här…',
  disabled = false,
  minHeightPx = 120,
  warnBelow,
  warnMessage,
  id,
}: FarmlyRichTextFieldProps) {
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Track whether we're doing a programmatic update to avoid echo
  const suppressUpdate = useRef(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        blockquote: false,
        codeBlock: false,
        code: false,
        horizontalRule: false,
        hardBreak: false,
      }),
      HardBreak.configure({ keepMarks: true }),
      Placeholder.configure({ placeholder }),
    ],
    content: farmlyMarkdownToHtml(value),
    editable: !disabled,
    onUpdate: ({ editor: e }) => {
      if (suppressUpdate.current) return;
      const md = htmlToFarmlyMarkdown(e.getHTML());
      onChangeRef.current(md);
    },
    editorProps: {
      attributes: {
        class: 'farmly-richtext focus:outline-none',
        style: `min-height:${minHeightPx}px`,
        ...(id ? { id } : {}),
      },
      // Strip disallowed content on paste
      handlePaste: (_view, event) => {
        // Let TipTap handle it — StarterKit config already strips disallowed nodes
        // But strip raw HTML from plain text paste
        const html = event.clipboardData?.getData('text/html');
        if (!html) {
          // Plain text paste — let TipTap handle normally
          return false;
        }
        // HTML paste — TipTap will parse with the restricted schema,
        // automatically dropping disallowed elements (headings, images, etc.)
        return false;
      },
    },
  });

  // Sync external value changes (form reset, undo, etc.)
  useEffect(() => {
    if (!editor || editor.isFocused) return;

    const currentMd = htmlToFarmlyMarkdown(editor.getHTML());
    if (currentMd === value) return;

    suppressUpdate.current = true;
    editor.commands.setContent(farmlyMarkdownToHtml(value), { emitUpdate: false });
    suppressUpdate.current = false;
  }, [editor, value]);

  // Update editable state
  useEffect(() => {
    if (editor) editor.setEditable(!disabled);
  }, [editor, disabled]);

  const charLen = farmlyMarkdownTextLength(value);

  if (!editor) return null;

  return (
    <div className="space-y-1">
      {/* Header: label */}
      <Label htmlFor={id} className="text-xs">
        {label}
      </Label>

      {/* Editor with toolbar */}
      <div
        className={cn(
          'w-full min-w-0 max-w-full rounded-lg overflow-hidden bg-card shadow-sm focus-within:ring-2 focus-within:ring-border-focus focus-within:shadow transition-all',
          disabled && 'opacity-50 pointer-events-none',
        )}
      >
        {/* Toolbar */}
        <div className="flex w-full min-w-0 max-w-full flex-wrap items-center gap-0.5 p-1.5 bg-muted">
          <ToolbarButton
            active={editor.isActive('bold')}
            onMouseDown={() => editor.chain().focus().toggleBold().run()}
            title="Fet (Ctrl+B)"
            disabled={disabled}
          >
            <Bold className="h-3.5 w-3.5" />
          </ToolbarButton>

          <ToolbarButton
            active={editor.isActive('italic')}
            onMouseDown={() => editor.chain().focus().toggleItalic().run()}
            title="Kursiv (Ctrl+I)"
            disabled={disabled}
          >
            <Italic className="h-3.5 w-3.5" />
          </ToolbarButton>

          <div className="w-px h-5 bg-border-subtle mx-0.5" />

          <ToolbarButton
            active={editor.isActive('bulletList')}
            onMouseDown={() => editor.chain().focus().toggleBulletList().run()}
            title="Punktlista"
            disabled={disabled}
          >
            <List className="h-3.5 w-3.5" />
          </ToolbarButton>

          <ToolbarButton
            active={editor.isActive('orderedList')}
            onMouseDown={() => editor.chain().focus().toggleOrderedList().run()}
            title="Numrerad lista"
            disabled={disabled}
          >
            <ListOrdered className="h-3.5 w-3.5" />
          </ToolbarButton>
        </div>

        {/* Editor content */}
        <EditorContent editor={editor} />
      </div>

      {/* Footer: warnings + char count */}
      <div className="flex min-w-0 flex-wrap justify-between gap-1 text-xs text-muted-foreground">
        <span>
          {warnBelow && charLen > 0 && charLen < warnBelow && (
            <span className="text-warning">
              {warnMessage ?? `Kort – sikta på minst ${warnBelow} tecken.`}
            </span>
          )}
        </span>
        {maxLength && (
          <span className={cn('shrink-0', charLen > maxLength ? 'text-destructive' : '')}>
            {charLen}/{maxLength}
          </span>
        )}
      </div>
    </div>
  );
}

/** Small toolbar button matching Farmly calm aesthetic. */
function ToolbarButton({
  active,
  onMouseDown,
  title,
  disabled,
  children,
}: {
  active: boolean;
  onMouseDown: () => void;
  title: string;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className={cn(
        'h-7 w-7',
        active
          ? 'bg-card text-foreground'
          : 'text-muted-foreground hover:text-foreground',
      )}
      onMouseDown={(e) => {
        e.preventDefault();
        onMouseDown();
      }}
      title={title}
      disabled={disabled}
      tabIndex={-1}
    >
      {children}
    </Button>
  );
}
